(() => {
  const frameParam = 'rv15_frame';
  const shellVersion = 'v23-1to1';
  const effects = ['rise', 'left', 'right', 'zoom', 'blur', 'tilt'];
  const revealSelector = [
    '.rv15-home',
    '.rv15-section-head',
    '.rv15-trust-card',
    '.rv15-product-card',
    '.rv15-stock-card',
    '.rv15-series-card',
    '.rv15-brand-card',
    '.rv15-guide-card',
    '.rv15-proof-card',
    '.section-head',
    '.trust-item',
    '.feature-card',
    '.stock-card',
    '.series-card',
    '.brand-card',
    '.guide-card',
    '.proof-card'
  ].join(',');
  const previewOverlaySelector = [
    '.kl-teaser-VyWd7g',
    '[class*="kl-teaser"]',
    '[class*="kl-private-reset-css"]',
    '[id*="klaviyo"]',
    '[class*="klaviyo"]',
    '.needsclick',
    '[id^="shopify-pc"]',
    '[class*="shopify-pc__"]',
    '#age-check-overlay',
    '[id*="age-check"]',
    '[class*="age-check"]',
    '[id*="age-verification"]',
    '[class*="age-verification"]',
    '[id*="privacy-banner"]',
    '[class*="privacy-banner"]'
  ].join(',');

  const motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function removePreviewOverlays() {
    document.querySelectorAll(previewOverlaySelector).forEach((node) => {
      if (['HTML', 'HEAD', 'BODY'].includes(node.tagName)) return;
      node.remove();
    });
    document.documentElement.classList.remove('klaviyo-prevent-body-scrolling');
    document.body?.classList.remove('klaviyo-prevent-body-scrolling');
  }

  function initPreviewOverlayCleanup() {
    if (document.documentElement.dataset.rv15OverlayCleanupReady) return;
    document.documentElement.dataset.rv15OverlayCleanupReady = 'true';

    removePreviewOverlays();
    [250, 900, 1800, 3600, 6000].forEach((delay) => {
      window.setTimeout(removePreviewOverlays, delay);
    });

    const observer = new MutationObserver(() => removePreviewOverlays());
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function buildFrameUrl() {
    const frameUrl = new URL(window.location.href);
    if (!frameUrl.searchParams.get('v')) frameUrl.searchParams.set('v', shellVersion);
    frameUrl.searchParams.set(frameParam, '1');
    frameUrl.searchParams.delete('rv15_device');
    return frameUrl.toString();
  }

  function absoluteStylesheetMarkup() {
    const seen = new Set();
    return Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => {
        const clone = node.cloneNode(true);
        if (clone.tagName === 'LINK') {
          const href = clone.href;
          if (!href || seen.has(href)) return '';
          seen.add(href);
          clone.setAttribute('href', href);
        }
        return clone.outerHTML;
      })
      .filter(Boolean)
      .join('\n');
  }

  function sanitizedFrameBody() {
    const clone = document.body.cloneNode(true);

    clone.classList.remove('device-preview-mode', 'rv15-preview-shell-page', 'klaviyo-prevent-body-scrolling');
    clone.classList.add('rv15-frame-page');

    clone.querySelectorAll('script, style, link, iframe, .rv15-preview-shell-root').forEach((node) => node.remove());
    clone.querySelectorAll('*').forEach((node) => {
      const signature = `${node.id || ''} ${node.className || ''}`.toLowerCase();
      if (
        signature.includes('klaviyo') ||
        signature.includes('newsletter-popup') ||
        signature.includes('modal-newsletter') ||
        signature.includes('discount-popup') ||
        signature.includes('age-check') ||
        signature.includes('age-verification') ||
        signature.includes('shopify-pc') ||
        signature.includes('privacy-banner')
      ) {
        node.remove();
      }
    });

    return {
      className: clone.className,
      html: clone.innerHTML
    };
  }

  function safeFrameScriptMarkup() {
    const seen = new Set();

    return Array.from(document.querySelectorAll('script[src]'))
      .map((node) => {
        const src = node.src;
        const sections = node.dataset ? node.dataset.sections || '' : '';
        const isThemeSectionsScript = (
          node.id === 'sections-script' ||
          sections.includes('super-hero-banner') ||
          src.includes('/compiled_assets/scripts.js')
        );

        if (!src || !isThemeSectionsScript || seen.has(src)) return '';
        seen.add(src);

        const clone = document.createElement('script');
        clone.src = src;
        clone.defer = true;
        clone.setAttribute('data-rv15-frame-safe-script', 'sections');
        return clone.outerHTML;
      })
      .filter(Boolean)
      .join('\n');
  }

  function buildFrameDocument() {
    const viewport = document.querySelector('meta[name="viewport"]')?.outerHTML || '<meta name="viewport" content="width=device-width, initial-scale=1">';
    const script = document.querySelector('script[src*="roshi-home-v15.js"]');
    const scriptSrc = script ? script.src : '';
    const safeScripts = safeFrameScriptMarkup();
    const body = sanitizedFrameBody();

    return `<!doctype html>
<html class="rv15-frame-root">
<head>
  <meta charset="utf-8">
  ${viewport}
  <base href="${window.location.origin}/">
  ${absoluteStylesheetMarkup()}
</head>
<body class="${body.className}">
  ${body.html}
  <script>window.__RV15_FRAME__ = true;<\/script>
  ${safeScripts}
  ${scriptSrc ? `<script src="${scriptSrc}" defer><\/script>` : ''}
</body>
</html>`;
  }

  function initPreviewShell() {
    const url = new URL(window.location.href);
    const isEmbeddedFrame = Boolean(window.__RV15_FRAME__ || (window.frameElement && window.frameElement.dataset.rv15Frame === '1'));

    if (url.searchParams.get(frameParam) === '1' || isEmbeddedFrame) {
      document.documentElement.classList.add('rv15-frame-root');
      document.body.classList.add('rv15-frame-page');
      initPreviewOverlayCleanup();
      return false;
    }

    const shouldUsePreviewShell = url.searchParams.get('rv15_preview_shell') === '1';
    if (!shouldUsePreviewShell) {
      return false;
    }

    const device = url.searchParams.get('rv15_device') || 'split';
    const frameSrc = buildFrameUrl();
    const frameDocument = buildFrameDocument();
    const shell = document.createElement('div');
    shell.className = 'device-preview-stage rv15-preview-shell-root';
    shell.innerHTML = `
      <div class="device-preview-top">
        <div class="device-preview-title">
          <b>V15 Desktop + Phone Preview</b>
          <span>Use the buttons to compare the full desktop homepage and the real 390px phone layout.</span>
        </div>
        <div class="device-controls" aria-label="Preview device switcher">
          <button type="button" data-device-button="desktop">Desktop</button>
          <button type="button" data-device-button="phone">Phone</button>
          <button type="button" data-device-button="split">Side by Side</button>
        </div>
      </div>
      <div class="device-preview-grid">
        <section class="device-panel desktop-panel" aria-label="Desktop preview">
          <div class="device-label"><b>Desktop</b><span>1180px viewport</span></div>
          <div class="desktop-shell">
            <iframe title="Roshi desktop homepage preview" data-rv15-frame="1" data-frame-src="${frameSrc}"></iframe>
          </div>
        </section>
        <section class="device-panel phone-panel" aria-label="Phone preview">
          <div class="device-label"><b>Phone</b><span>390px viewport</span></div>
          <div class="phone-shell">
            <iframe title="Roshi phone homepage preview" data-rv15-frame="1" data-frame-src="${frameSrc}"></iframe>
          </div>
        </section>
      </div>
    `;

    document.documentElement.classList.add('rv15-preview-shell-root-page');
    document.body.classList.add('device-preview-mode', 'rv15-preview-shell-page');
    document.body.dataset.device = ['desktop', 'phone', 'split'].includes(device) ? device : 'split';
    initPreviewOverlayCleanup();

    Array.from(document.body.children).forEach((child) => {
      if (['SCRIPT', 'STYLE', 'LINK'].includes(child.tagName)) return;
      child.classList.add('rv15-preview-hidden');
    });

    document.body.appendChild(shell);
    shell.querySelectorAll('iframe[data-rv15-frame="1"]').forEach((iframe) => {
      iframe.srcdoc = frameDocument;
    });
    shell.querySelectorAll('[data-device-button]').forEach((button) => {
      button.addEventListener('click', () => {
        document.body.dataset.device = button.dataset.deviceButton;
      });
    });

    return true;
  }

  function initScrollProgress() {
    if (document.documentElement.dataset.rv15ProgressReady) return;
    document.documentElement.dataset.rv15ProgressReady = 'true';

    const update = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const progress = maxScroll > 0 ? (window.scrollY / maxScroll) * 100 : 0;
      document.documentElement.style.setProperty('--rv15-scroll-progress', `${Math.min(100, Math.max(0, progress))}%`);
      document.documentElement.style.setProperty('--scroll-progress', `${Math.min(100, Math.max(0, progress))}%`);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  function initCursorGlow() {
    if (document.documentElement.dataset.rv15CursorReady || !window.matchMedia('(pointer: fine)').matches) return;
    document.documentElement.dataset.rv15CursorReady = 'true';

    window.addEventListener('pointermove', (event) => {
      document.body.classList.add('rv15-pointer-active');
      document.body.classList.add('is-pointer-active');
      document.documentElement.style.setProperty('--rv15-mouse-x', `${event.clientX}px`);
      document.documentElement.style.setProperty('--rv15-mouse-y', `${event.clientY}px`);
      document.documentElement.style.setProperty('--mouse-x', `${event.clientX}px`);
      document.documentElement.style.setProperty('--mouse-y', `${event.clientY}px`);
    }, { passive: true });

    window.addEventListener('pointerleave', () => {
      document.body.classList.remove('rv15-pointer-active');
      document.body.classList.remove('is-pointer-active');
    });
  }

  function initReveals(scope = document) {
    const targets = Array.from(scope.querySelectorAll(revealSelector)).filter((target) => !target.dataset.rv15RevealReady);
    if (!targets.length) return;

    targets.forEach((target, index) => {
      const effect = target.dataset.rv15Effect || effects[index % effects.length];
      target.dataset.rv15RevealReady = 'true';
      target.classList.add('rv15-reveal', `rv15-fx-${effect}`);
      target.classList.add('reveal', `fx-${effect}`);
      target.style.setProperty('--rv15-delay', `${Math.min(index % 6, 5) * 70}ms`);
      target.style.setProperty('--delay', `${Math.min(index % 6, 5) * 70}ms`);
    });

    if (motionReduced || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });

    targets.forEach((target) => observer.observe(target));
  }

  function initMagneticCards(scope = document) {
    scope.querySelectorAll('.rv15-magnetic').forEach((card) => {
      if (card.dataset.rv15MagneticReady) return;
      card.dataset.rv15MagneticReady = 'true';
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--rv15-spot-x', `${event.clientX - rect.left}px`);
        card.style.setProperty('--rv15-spot-y', `${event.clientY - rect.top}px`);
        card.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
        card.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
      }, { passive: true });
    });
  }

  function initSeries(scope = document) {
    scope.querySelectorAll('[data-rv15-series-grid]').forEach((grid) => {
      if (grid.dataset.rv15SeriesReady) return;
      grid.dataset.rv15SeriesReady = 'true';
      const cards = Array.from(grid.querySelectorAll('.rv15-series-card, .series-card'));
      const isTouchRoute = () => (
        window.innerWidth <= 680 ||
        window.matchMedia('(hover: none)').matches ||
        window.matchMedia('(pointer: coarse)').matches
      );

      const activate = (card) => {
        grid.classList.add('has-active');
        cards.forEach((item) => item.classList.toggle('is-active', item === card));
      };

      const clear = () => {
        grid.classList.remove('has-active');
        cards.forEach((item) => {
          item.classList.remove('is-active');
          delete item.dataset.rv15TouchPreview;
        });
      };

      cards.forEach((card) => {
        card.addEventListener('pointerenter', () => {
          if (isTouchRoute()) return;
          activate(card);
        });
        card.addEventListener('mouseover', () => {
          if (isTouchRoute()) return;
          activate(card);
        });
        card.addEventListener('focus', () => {
          if (isTouchRoute()) return;
          activate(card);
        });
        card.addEventListener('touchstart', () => {
          if (isTouchRoute()) {
            clear();
            return;
          }
          activate(card);
        }, { passive: true });
        card.addEventListener('click', (event) => {
          if (isTouchRoute()) return;
          if (card.dataset.rv15TouchPreview === 'true' || !card.classList.contains('is-active')) {
            event.preventDefault();
            delete card.dataset.rv15TouchPreview;
            activate(card);
          }
        });
      });

      grid.addEventListener('pointerleave', () => {
        if (!isTouchRoute()) clear();
      });
      grid.addEventListener('focusout', (event) => {
        if (!grid.contains(event.relatedTarget)) clear();
      });
    });
  }

  function handleBrandCarouselWheel(event) {
    if (event.ctrlKey || event.shiftKey || event.defaultPrevented) return;
    if (!event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    const deltaUnit = event.deltaMode === 1
      ? 16
      : event.deltaMode === 2
        ? window.innerHeight
        : 1;

    event.preventDefault();
    window.scrollBy({
      top: event.deltaY * deltaUnit,
      left: 0,
      behavior: 'auto'
    });
  }

  function initBrandCarousel(scope = document) {
    scope.querySelectorAll('[data-rv15-carousel-shell], [data-rv15-brand-shell]').forEach((shell) => {
      if (shell.dataset.rv15CarouselReady) return;
      shell.dataset.rv15CarouselReady = 'true';

      const track = shell.querySelector('[data-rv15-carousel-track], [data-rv15-brand-track], [data-brand-track], [data-rv15-product-carousel]');
      const prev = shell.querySelector('[data-rv15-carousel-prev], [data-rv15-brand-prev], [data-brand-prev]');
      const next = shell.querySelector('[data-rv15-carousel-next], [data-rv15-brand-next], [data-brand-next]');
      if (!track || !prev || !next) return;

      const setState = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const hasOverflow = maxScroll > 4;
        shell.classList.toggle('rv15-carousel-has-overflow', hasOverflow);
        prev.disabled = !hasOverflow || track.scrollLeft <= 4;
        next.disabled = !hasOverflow || track.scrollLeft >= maxScroll - 4;
      };

      const scroll = (direction) => {
        const amount = Math.max(track.clientWidth * 0.78, 320);
        track.scrollBy({ left: direction * amount, behavior: motionReduced ? 'auto' : 'smooth' });
        window.setTimeout(setState, motionReduced ? 30 : 420);
      };

      prev.addEventListener('click', () => scroll(-1));
      next.addEventListener('click', () => scroll(1));
      if (track.matches('[data-rv15-brand-track], [data-brand-track]')) {
        track.addEventListener('wheel', handleBrandCarouselWheel, { passive: false });
      }
      track.addEventListener('scroll', setState, { passive: true });
      window.addEventListener('resize', setState, { passive: true });
      window.setTimeout(setState, 80);
      window.setTimeout(setState, 600);
    });
  }

  function initProofLightbox() {
    if (document.documentElement.dataset.rv15ProofLightboxReady) return;
    document.documentElement.dataset.rv15ProofLightboxReady = 'true';

    let lightbox = document.querySelector('[data-rv15-proof-lightbox-modal]');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'rv15-proof-lightbox';
      lightbox.setAttribute('data-rv15-proof-lightbox-modal', '');
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Buyer photo preview');
      lightbox.hidden = true;
      lightbox.innerHTML = [
        '<button class="rv15-proof-lightbox__backdrop" type="button" data-rv15-proof-lightbox-close aria-label="Close preview"></button>',
        '<div class="rv15-proof-lightbox__panel">',
        '<button class="rv15-proof-lightbox__close" type="button" data-rv15-proof-lightbox-close aria-label="Close preview">×</button>',
        '<img class="rv15-proof-lightbox__image" alt="">',
        '</div>'
      ].join('');
      document.body.appendChild(lightbox);
    }

    const image = lightbox.querySelector('.rv15-proof-lightbox__image');
    const closeButton = lightbox.querySelector('.rv15-proof-lightbox__close');

    const open = (trigger) => {
      const source = trigger.dataset.proofFullSrc || trigger.querySelector('img')?.currentSrc || trigger.querySelector('img')?.src;
      if (!source || !image) return;
      image.src = source;
      image.alt = trigger.dataset.proofAlt || trigger.querySelector('img')?.alt || 'Buyer photo preview';
      lightbox.hidden = false;
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
      document.documentElement.classList.add('rv15-proof-lightbox-open');
      closeButton?.focus({ preventScroll: true });
    };

    const close = () => {
      lightbox.classList.remove('is-open');
      document.documentElement.classList.remove('rv15-proof-lightbox-open');
      window.setTimeout(() => {
        if (!lightbox.classList.contains('is-open')) {
          lightbox.hidden = true;
          if (image) image.removeAttribute('src');
        }
      }, 220);
    };

    document.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-rv15-proof-lightbox-close]');
      if (closeTrigger) {
        close();
        return;
      }

      const trigger = event.target.closest('[data-rv15-proof-lightbox]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !lightbox.hidden) {
        close();
        return;
      }

      if (event.key !== 'Enter' && event.key !== ' ') return;
      const trigger = event.target.closest('[data-rv15-proof-lightbox]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger);
    });
  }

  function initFloating(scope = document) {
    scope.querySelectorAll('.rv15-product-card, .rv15-trust-card, .feature-card, .trust-item').forEach((card, index) => {
      if (card.dataset.rv15FloatReady) return;
      card.dataset.rv15FloatReady = 'true';
      card.classList.add('rv15-float');
      card.style.animationDelay = `${(index % 4) * 0.25}s`;
    });
  }

  function initImages(scope = document) {
    scope.querySelectorAll('.rv15-home img').forEach((image) => {
      if (image.complete) {
        image.classList.add('is-loaded');
      } else {
        image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
      }
    });
  }

  function init(scope = document) {
    if (scope === document && initPreviewShell()) return;
    if (scope === document && document.querySelector('.rv15-home')) {
      document.documentElement.classList.add('rv15-real-home-root');
      document.body.classList.add('rv15-real-home-page');
    }
    initScrollProgress();
    initCursorGlow();
    initReveals(scope);
    initMagneticCards(scope);
    initSeries(scope);
    initBrandCarousel(scope);
    initProofLightbox(scope);
    initFloating(scope);
    initImages(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
